import React, { useState, useEffect, FC, useRef } from 'react';
import { View, Text, Animated, TextStyle, TouchableOpacity, LayoutChangeEvent } from 'react-native';
import { connect } from 'react-redux';

import { isPhone, isWeb, machineStatusColorName } from '@helpers';
import { scale } from '@components/frame';
import ColorWrapper, { checkColor } from '@styles/colors';
import { font, FontStyles } from '@styles/fonts';
import Svg from '@styles/icons/svg';

import TouchableElement from '@components/common/touchableElement';

import GroupSelectModal from '@components/machines/components/modals/groupSelectModal';
import { getMachinesGroup } from '@reducers/machines';
import { ColorFunction } from '@styles/colors/types';
import { ItemsListWrapperProps } from '@v2/pages/machines/common/types/props';
import MachinesHeader from '@v2/pages/machines/common/components/Header/MachinesHeaderV2/MachinesHeaderV2';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { useNavigation } from '@react-navigation/native';
import { MACHINES_DETAILS_SECRET_SCREEN, MACHINES_MAIN_SCREEN } from '@v2/pages/machines/navigation/config';
import { Machine } from '@interfaces/machines';
import { PHONE_DETAILS_HEADER_GAP } from '@v2/pages/common/constants/base.constants';
import { useHandleNavigationMachinesHeader } from '@v2/pages/machines/hooks/useHandleNavigationMachineHeader';
import { useDeviceSize } from '@v2/pages/common/hooks/useDeviceSize';

interface HeaderProps extends Partial<Pick<ItemsListWrapperProps, 'selectedGroup' | 'setSelectedGroup'>> {
  text: string;
  C: ColorFunction;
  fadeAnim?: any;
  marginTop: number;
  dummy?: boolean;
  animationDuration?: number;
  getMachinesGroup: any;
  bg?: string;
  fontType?: keyof FontStyles;
  fontWeight?: any;
  selectedItem: Machine;
}

const Header: FC<HeaderProps> = ({
  text,
  C,
  fadeAnim,
  marginTop,
  dummy,
  animationDuration,
  getMachinesGroup,
  selectedGroup,
  setSelectedGroup,
  bg = 'Background / Light / System',
  fontType = 'Large Title',
  fontWeight,
  selectedItem,
}) => {
  const textOffset = isPhone ? 40 : 0;

  const { isPerformanceSubScreen, isMachinesScreen } = useHandleNavigationMachinesHeader();
  const { IS_NARROW_WEB, IS_WIDE_WEB } = useDeviceSize();

  const [groups, setGroups] = useState([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [groupModalPosition, setGroupModalPosition] = useState({ top: 0, left: 0 });

  const buttonRef = useRef<View>(null);

  const handleMeasure = () => {
    buttonRef.current?.measureInWindow((x, y, width, height) => {
      setGroupModalPosition({ left: x + (isPhone ? 15 : 12), top: y + height + 5 });
    });
  };

  const secretScreen = useRef<number>(0);
  const navigation = useNavigation<any>();

  const { isAllSelected, id } = useHandleParams();

  useEffect(() => {
    if (dummy) {
      return;
    }

    (async () => {
      const g = await getMachinesGroup();
      setGroups(g || []);
      setGroupsLoaded(true);
    })();
  }, []);

  const goToSecret = () => {
    if (!isPhone || isAllSelected) return;

    if (secretScreen.current === 4) {
      secretScreen.current = 0;
      navigation.navigate(`${MACHINES_MAIN_SCREEN}${MACHINES_DETAILS_SECRET_SCREEN}`, { id });
    } else {
      secretScreen.current += 1;
    }
  };

  const onTitlePress = () => {
    if (isPerformanceSubScreen && isMachinesScreen && !isPhone) return;
    if (groupsLoaded) {
      setIsGroupModalOpen(!isGroupModalOpen);
    }
  };

  return (
    <Animated.View
      style={[
        {
          borderBottomWidth: 0,
          marginTop: marginTop,
          marginBottom: scale(isPhone ? 6 : 12),
          borderBottomColor: C('Hairline / Light / No trans'),
          backgroundColor: C(bg),
          opacity: dummy ? 0 : 1,
          zIndex: 100000,
        },
      ]}
    >
      <Animated.View
        style={{
          opacity: dummy
            ? 0
            : fadeAnim.interpolate({
                inputRange: [0, animationDuration, animationDuration],
                outputRange: [1, 1, 0],
              }),
          zIndex: 100000,
        }}
      >
        {text && (
          <View style={{}}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }} ref={buttonRef} onLayout={handleMeasure}>
              <TouchableElement hitSlop={{ top: scale(5), bottom: scale(5), left: scale(5), right: scale(5) }} onPress={onTitlePress}>
                {({ boxStyle }) => (
                  <View
                    style={[
                      {
                        marginLeft: scale(isAllSelected && isPhone ? 16 : 13),
                        paddingLeft: scale(5),
                        flexDirection: 'row',
                        gap: scale(9),
                        alignItems: 'center',
                        zIndex: 100000,
                        alignSelf: 'flex-start',
                        paddingRight: scale(10),
                      },
                      isWeb && { cursor: 'pointer' },
                    ]}
                  >
                    {!isAllSelected && isPhone && (
                      <View
                        style={{
                          backgroundColor: selectedItem?.machineStatus?.status
                            ? C(machineStatusColorName[selectedItem.machineStatus.status])
                            : 'transparent',
                          height: scale(12.5),
                          width: scale(12.5),
                          borderRadius: scale(12.5 / 2),
                          opacity: selectedItem?.isProcessingPI || selectedItem?.machineStatisticsOnly ? 0 : 1,
                        }}
                      />
                    )}

                    <Text style={[font[fontType], { color: C('Label / Light / Primary') }, fontWeight ? { fontWeight } : {}]}>
                      {selectedGroup ? selectedGroup.name : text}
                    </Text>
                    {isGroupModalOpen && groupsLoaded && (
                      <GroupSelectModal
                        {...{
                          C,
                          groups,
                          selectedGroup,
                          setSelectedGroup,
                          setIsGroupModalOpen,
                          isVisible: isGroupModalOpen,
                          position: groupModalPosition,
                        }}
                      />
                    )}
                  </View>
                )}
              </TouchableElement>
              <TouchableElement onPress={onTitlePress}>
                {({ boxStyle }) => (
                  <View
                    style={[
                      boxStyle,
                      {
                        backgroundColor: isGroupModalOpen ? C(checkColor('Button/Light/Secondary', 'pressed')) : C('Button/Light/Secondary'),
                        borderRadius: scale(8),
                        width: scale(17),
                        height: scale(17),
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginTop: scale(IS_WIDE_WEB ? 8 : isPhone && !isAllSelected ? 2 : IS_NARROW_WEB && isAllSelected ? 8 : 0),
                        zIndex: 100000,
                      },
                    ]}
                  >
                    <View style={[boxStyle, { marginTop: scale(2) }]}>
                      <Svg icon="chevronBottom" height={scale(6)} width={scale(9)} color={C('Button/Light/Secondary Text')} />
                    </View>
                  </View>
                )}
              </TouchableElement>
            </View>
            {/* {!isAllSelected && isPhone && (
              <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary'), marginLeft: scale(40) }]}>
                {userInfo?.userProfile?.firstName} {userInfo?.userProfile?.lastName}
              </Text>
            )} */}
          </View>
        )}

        {isPhone && (
          <Animated.View
            style={{
              marginTop: scale(PHONE_DETAILS_HEADER_GAP * 1.5),
              width: '100%',
              zIndex: dummy ? 0 : 1000,
              transform: [
                {
                  translateY: dummy
                    ? -200
                    : fadeAnim.interpolate({
                        inputRange: [0, animationDuration, animationDuration],
                        outputRange: [0, 0, -200],
                      }),
                },
              ],
            }}
          >
            <MachinesHeader panelViewMode={false} noMachine={false} />
          </Animated.View>
        )}
      </Animated.View>
      <Animated.View
        style={{
          opacity: dummy
            ? 0
            : fadeAnim.interpolate({
                inputRange: [0, animationDuration, animationDuration],
                outputRange: [0, 0, 1],
              }),
          position: 'absolute',
          width: '100%',
          bottom: scale(0),
          transform: [
            {
              translateY: dummy
                ? textOffset
                : fadeAnim.interpolate({
                    inputRange: [0, animationDuration, animationDuration],
                    outputRange: [textOffset, 0, 0],
                  }),
            },
          ],
        }}
      >
        <View>
          <Text style={[font['Headline'], { color: C('Label / Light / Primary'), textAlign: 'center' }]}>{text}</Text>
        </View>
      </Animated.View>
    </Animated.View>
  );
};

export default connect(({ machines: { selectedItem } }) => ({ selectedItem }), { getMachinesGroup })(ColorWrapper(Header));
